/**
 * MakeMyTrip + Yatra + IRCTC + EaseMyTrip + Goibibo booking parser. Reads
 * Gmail booking emails for flight/train/bus/hotel/cab.
 *
 * Dial uses travel-partners to enrich social graph.
 * Almari uses upcoming travel destinations for vacation-wear seasonality.
 */
import type { TravelBooking, Provenance } from './types.ts';
import { paise } from './types.ts';
import { MOCK_BOOKINGS } from './mock-data.ts';
import { ensureFreshToken, gmailFetch, type OAuthToken, GmailOAuth } from './gmail-oauth.ts';

interface GmailListResp { messages?: ReadonlyArray<{ id: string }>; nextPageToken?: string; }
interface GmailMsgResp { id: string; internalDate: string; payload: { headers: ReadonlyArray<{ name: string; value: string }>; body?: { data?: string }; }; }

const QUERIES: ReadonlyArray<{ q: string; merchant: TravelBooking['merchant']; default_kind: TravelBooking['kind'] }> = [
  { q: 'from:(trips@makemytrip.com OR noreply@makemytrip.com)', merchant: 'mmt', default_kind: 'flight' },
  { q: 'from:(noreply@yatra.com OR support@yatra.com)', merchant: 'yatra', default_kind: 'flight' },
  { q: 'from:(ticketadmin@irctc.co.in OR autobooking@irctc.co.in)', merchant: 'irctc', default_kind: 'train' },
  { q: 'from:(care@easemytrip.com OR alerts@easemytrip.com)', merchant: 'easemytrip', default_kind: 'flight' },
  { q: 'from:(notify@goibibo.com OR support@goibibo.com)', merchant: 'goibibo', default_kind: 'hotel' },
];

export interface TravelOpts {
  readonly mode: 'mock' | 'live';
  readonly oauth?: GmailOAuth;
  readonly token?: OAuthToken;
  readonly since_days?: number;
  readonly max?: number;
}

export async function fetchTravel(opts: TravelOpts): Promise<readonly TravelBooking[]> {
  if (opts.mode === 'mock') return MOCK_BOOKINGS;
  if (!opts.oauth || !opts.token) throw new Error('live mode requires oauth + token');

  const token = await ensureFreshToken(opts.oauth, opts.token);
  const since = opts.since_days ?? 730;
  const max = opts.max ?? 120;
  const out: TravelBooking[] = [];

  for (const def of QUERIES) {
    const q = `${def.q} after:${Math.floor((Date.now() - since * 86_400_000) / 1000)}`;
    const listUrl = new URL('https://gmail.googleapis.com/gmail/v1/users/me/messages');
    listUrl.searchParams.set('q', q);
    listUrl.searchParams.set('maxResults', '50');
    const list = await gmailFetch<GmailListResp>(token, listUrl.toString());
    for (const m of list.messages ?? []) {
      const msg = await gmailFetch<GmailMsgResp>(
        token,
        `https://gmail.googleapis.com/gmail/v1/users/me/messages/${m.id}?format=full`,
      );
      const parsed = parse(def.merchant, def.default_kind, msg);
      if (parsed) out.push(parsed);
      if (out.length >= max) break;
    }
  }
  return out;
}

function parse(merchant: TravelBooking['merchant'], default_kind: TravelBooking['kind'], msg: GmailMsgResp): TravelBooking | null {
  const data = msg.payload.body?.data;
  if (!data) return null;
  let text: string;
  try { text = atob(data.replace(/-/g, '+').replace(/_/g, '/')); } catch { return null; }

  const pnr =
    matchStr(text, /(?:PNR|Booking\s*(?:ID|Ref(?:erence)?))[:\s]*([A-Z0-9]{5,12})/i) ?? `${merchant}-${msg.id.slice(0, 8)}`;
  const dest = matchStr(text, /(?:Destination|To|Drop)[:\s]+([A-Z]{3}|[A-Z][A-Za-z\s.&,'-]{2,40})/i);
  const cost = matchNum(text, /(?:Total\s*(?:Amount|Fare|Cost)|Amount\s*Paid|Grand\s*Total)[:\s]*(?:Rs\.?|INR|₹)\s*([0-9,]+(?:\.[0-9]{1,2})?)/i);
  const kindMatch = matchStr(text, /\b(flight|train|bus|hotel|cab)\b/i);
  const kind = (kindMatch?.toLowerCase() as TravelBooking['kind']) ?? default_kind;

  const start = matchDate(text, /(?:Travel\s*Date|Check[-\s]*in|Departure|Boarding)[:\s]*([A-Za-z0-9,\s:-]{5,30})/i);
  const end = matchDate(text, /(?:Return\s*Date|Check[-\s]*out|Arrival)[:\s]*([A-Za-z0-9,\s:-]{5,30})/i);
  const travellers = matchAll(text, /(?:Traveller|Passenger|Guest|Name)[:\s]+([A-Z][A-Za-z\s.]{2,40})/gi).slice(0, 6);

  if (!dest || !cost) return null;
  const provenance: Provenance = { connector: 'mmt-travel', source_id: msg.id, fetched_at: Date.now() };
  return {
    merchant,
    pnr,
    kind,
    start: start ?? new Date(Number(msg.internalDate)).toISOString(),
    ...(end ? { end } : {}),
    destination: dest.trim(),
    cost: paise(cost * 100),
    travellers: travellers.length ? travellers : ['(self)'],
    provenance,
  };
}

function matchStr(s: string, re: RegExp): string | null {
  const m = s.match(re);
  return m && m[1] ? m[1] : null;
}
function matchNum(s: string, re: RegExp): number | null {
  const m = s.match(re);
  return m && m[1] ? Number(m[1].replace(/,/g, '')) : null;
}
function matchDate(s: string, re: RegExp): string | null {
  const m = s.match(re);
  if (!m || !m[1]) return null;
  const d = new Date(m[1]);
  return Number.isFinite(d.valueOf()) ? d.toISOString() : null;
}
function matchAll(s: string, re: RegExp): string[] {
  const out: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(s)) !== null) if (m[1]) out.push(m[1].trim());
  return out;
}
